// Utilities and side-effecting helpers for the estimate → invoice link.
// Extracted from invoiceService.js to keep that file under the 500-line cap.
// These functions are concerned with the relationship between an estimate
// and its derived invoices: summing totals, counting linked invoices,
// resolving estimate status after invoice changes, and computing the next
// payment-sequence number for partial billing.
//
// invoiceService re-exports each function so existing call sites keep
// working with no import changes.

import { supabase } from '../supabase';
import { updateEstimate } from './estimateService';
import { invoicePreCreditTotal } from '../utils/invoice/invoicePreCreditTotal';
import { MAX_PAYMENT_PLAN_COUNT } from '../utils/estimate/paymentPlan';

const isVoided = (inv) => String(inv?.status || '').toLowerCase() === 'voided';

/** Same slack as partial convert in useEstimateCrud when comparing summed invoice totals to estimate.total */
const ESTIMATE_INVOICED_EPSILON = 0.02;

/** Max live invoices from one estimate: a payment plan has 1, 2 or 3 payments (KAN-1118). */
export const MAX_ESTIMATE_PAYMENT_INVOICES = MAX_PAYMENT_PLAN_COUNT;

/** Stable, locale-independent code for the payment-cap refusal. */
export const ESTIMATE_PAYMENT_CAP_CODE = 'ESTIMATE_PAYMENT_CAP_REACHED';

/**
 * i18n key for the cap message. Present in BOTH en.json and es.json.
 * The catalog strings hard-code the number 3 — if MAX_ESTIMATE_PAYMENT_INVOICES
 * ever changes, both translations change with it.
 */
export const ESTIMATE_PAYMENT_CAP_MESSAGE_KEY = 'billing.estimate.convert.error.paymentCapReached';

/**
 * Token embedded verbatim in the message a DB trigger raises when an
 * estimate-linked invoice write (INSERT or UPDATE) would bill more than the
 * estimate's total — same `RAISE EXCEPTION '<TOKEN>' USING ERRCODE = ...`
 * convention the credit RPCs use (see getRoleRpcErrorKey). This is a
 * server-side BACKSTOP behind the client-side guard math in
 * useEstimateConvertToInvoice — it catches the race the client can't see
 * (another session billing the remainder between this session's guard fetch
 * and its insert).
 */
export const OVER_ESTIMATE_TOTAL_CODE = 'OVER_ESTIMATE_TOTAL';

/** i18n key for the friendly over-total refusal (ships in both catalogs). */
export const OVER_ESTIMATE_TOTAL_MESSAGE_KEY = 'estimates.convert.errorOverTotal';

/** EN fallback paired with {@link OVER_ESTIMATE_TOTAL_MESSAGE_KEY} above. */
export const OVER_ESTIMATE_TOTAL_DEFAULT_MESSAGE =
  "This would bill more than the estimate's total. Someone may have already billed part of it — refresh and check the estimate.";

/**
 * The estimate status-transition trigger (20260814220000) raises this when a
 * stale client echo tries an illegal move — e.g. an admin has a draft-estimate
 * editor open, the nightly cron expires it, and the save then attempts
 * expired→draft. Correct DB behavior; the client must show "this changed,
 * refresh" instead of the raw token.
 */
export const INVALID_ESTIMATE_TRANSITION_CODE = 'INVALID_ESTIMATE_TRANSITION';
export const INVALID_ESTIMATE_TRANSITION_MESSAGE_KEY = 'estimates.edit.errorStaleStatus';

/**
 * The payment-cap refusal. Since the allocator returns `sequence: null` when
 * both slots are full, this is the NORMAL message a real user sees, not a
 * defensive edge case — so it MUST be localizable.
 *
 * Services have no `intl`, so this follows the established service-error
 * pattern (`getRoleRpcErrorKey`, `useEstimateExtendExpiration.resolveErrorKey`):
 * the service hands back a stable CODE and the React call site formats the
 * localized string via `getEstimateInvoiceErrorKey`. `message` keeps the
 * English sentence only as a last-resort fallback for a call site that prints
 * `error.message` raw — it can never be the localized copy.
 */
export const estimatePaymentCapError = () => {
  const error = new Error(
    `This estimate already has ${MAX_ESTIMATE_PAYMENT_INVOICES} payment invoices. Void an invoice to bill that payment again.`,
  );
  error.code = ESTIMATE_PAYMENT_CAP_CODE;
  error.i18nKey = ESTIMATE_PAYMENT_CAP_MESSAGE_KEY;
  return error;
};

/**
 * Map an error returned by the estimate path of `createInvoice` to an i18n key,
 * or null when it isn't one of ours (the caller keeps its generic message).
 * Accepts the Error object (preferred — carries `code`/`i18nKey`) or a bare
 * message string, so it works from a `catch` that only kept `err.message`.
 */
export function getEstimateInvoiceErrorKey(errorOrMessage) {
  if (!errorOrMessage) return null;
  if (typeof errorOrMessage === 'object') {
    if (typeof errorOrMessage.i18nKey === 'string') return errorOrMessage.i18nKey;
    if (errorOrMessage.code === ESTIMATE_PAYMENT_CAP_CODE) return ESTIMATE_PAYMENT_CAP_MESSAGE_KEY;
  }
  const message =
    typeof errorOrMessage === 'string' ? errorOrMessage : String(errorOrMessage?.message || '');
  // Also match the English fallback text so an older/serialized error (one that
  // crossed a boundary dropping custom props) still localizes.
  if (
    message.includes(ESTIMATE_PAYMENT_CAP_CODE) ||
    message.includes('payment invoices (first and second payment)') ||
    message.includes('payment invoices. Void an invoice')
  ) {
    return ESTIMATE_PAYMENT_CAP_MESSAGE_KEY;
  }
  // Server-side over-bill trigger (backstop behind the client guard fetch).
  // Also covers the credit-RELEASE path: releasing restores invoices.total UP
  // and is subject to the cap, so a release racing a re-bill aborts with this
  // same token — it must read friendly, not raw, wherever it surfaces.
  if (message.includes(OVER_ESTIMATE_TOTAL_CODE)) {
    return OVER_ESTIMATE_TOTAL_MESSAGE_KEY;
  }
  // Illegal estimate status move (stale client echo vs a concurrent
  // cron/recompute) — the transition rulebook trigger rejected it.
  if (message.includes(INVALID_ESTIMATE_TRANSITION_CODE)) {
    return INVALID_ESTIMATE_TRANSITION_MESSAGE_KEY;
  }
  return null;
}

/**
 * Sum non-voided invoice totals for a single estimate. Used in the
 * "Already invoiced" badge and the partial-conversion math. Voided invoices
 * MUST be excluded — otherwise voiding a paid invoice would still count
 * toward the estimate's invoiced total or the partial-conversion math,
 * otherwise the "Already invoiced" badge and the second-payment math both lie.
 *
 * Sums the PRE-CREDIT value of each invoice (`invoicePreCreditTotal`), not
 * the net `total` — account credit is a PAYMENT (or a price concession), not
 * a reduction in the work billed against the estimate. Crediting an invoice
 * must never re-open "remaining to bill" or make a fully-billed estimate look
 * partial again (credit-integrity, 2026-09-28).
 */
export function sumInvoiceTotalsForEstimate(invoices, estimateId) {
  if (!estimateId || !invoices?.length) return 0;
  return invoices
    .filter((i) => i.source_estimate_id === estimateId && !isVoided(i))
    .reduce((s, i) => s + invoicePreCreditTotal(i), 0);
}

/**
 * True when every NON-VOIDED invoice linked to this estimate has status
 * `paid`. Voided invoices are excluded from the check entirely. If the
 * surviving set is empty, returns false (nothing-to-archive guard).
 */
export function areAllLinkedInvoicesPaidForEstimate(invoices, estimateId) {
  if (!estimateId || !invoices?.length) return false;
  const linked = invoices.filter((i) => i.source_estimate_id === estimateId && !isVoided(i));
  if (linked.length === 0) return false;
  return linked.every((i) => (i.status || '').toLowerCase() === 'paid');
}

/** Count active (non-voided) invoices linked to an estimate — used by the
 *  3-invoice cap and the "next payment" branch in convert-to-invoice. */
export function countActiveInvoicesForEstimate(invoices, estimateId) {
  if (!estimateId || !invoices?.length) return 0;
  return invoices.filter((i) => i.source_estimate_id === estimateId && !isVoided(i)).length;
}

/**
 * Set estimate status from remaining linked invoices after a delete (or any
 * invoice change).
 * — No invoices → accepted
 * — Positive estimate total: sum within epsilon of total → invoiced;
 *   else partially_invoiced
 * — Zero estimate total: any linked invoice → invoiced (matches convert
 *   edge case)
 *
 * STATUS ONLY — it does NOT (and must not) compute paid-ness.
 * `estimates.fully_paid_at` is an orthogonal, SERVER-DERIVED signal owned by
 * recompute_estimate_fully_paid() (20260806120000), fired by triggers on both
 * `invoices` and `estimates.status`. So the `updateEstimate({ status })` below
 * already re-derives it as a side effect — no client-side computation belongs
 * here, and a direct client write of the column is rejected outright by the
 * estimates_guard_fully_paid_at_direct_write trigger.
 *
 * That derivation HAS to live in the DB, not here: this function is called from
 * exactly two places (voidInvoiceHandlers.js and useEstimateConvertToInvoice.js)
 * and neither admin's nor billing's mark-paid handlers call it — so a
 * client-driven implementation would miss the payment event entirely, which is
 * the one event the signal exists to observe.
 */
export async function syncEstimateStatusFromLinkedInvoices(estimateId, tenantId) {
  if (!estimateId || !tenantId) return;
  const { data: est, error: estErr } = await supabase
    .from('estimates')
    .select('id, total, status')
    .eq('id', estimateId)
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (estErr || !est) {
    if (estErr) console.warn('[syncEstimateStatusFromLinkedInvoices] load estimate', estErr);
    return;
  }

  // Exclude voided rows — they no longer count toward the estimate's
  // invoiced total. Without this filter, voiding a paid estimate-derived
  // invoice would leave the estimate stuck at 'invoiced' even though no
  // money was actually collected.
  //
  // The credit columns are selected so `sum` below can count each invoice at
  // its PRE-CREDIT value (invoicePreCreditTotal) — otherwise crediting a
  // fully-billed invoice would drop the live sum and flip a finished estimate
  // back to 'partially_invoiced', re-offering Convert on money already paid.
  const { data: invRows, error: invErr } = await supabase
    .from('invoices')
    .select('total, account_credit_applied, credit_applied_concession, credit_applied_pre_tax, taxable_base, tax_amount, tax_rate')
    .eq('tenant_id', tenantId)
    .eq('source_estimate_id', estimateId)
    .neq('status', 'voided')
    .is('deleted_at', null);
  if (invErr) {
    console.warn('[syncEstimateStatusFromLinkedInvoices] list invoices', invErr);
    return;
  }

  const rows = invRows || [];
  const sum = rows.reduce((s, i) => s + invoicePreCreditTotal(i), 0);
  const estTotal = Number(est.total) || 0;

  let nextStatus;
  if (rows.length === 0) {
    nextStatus = 'accepted';
  } else if (estTotal > 0.0001) {
    nextStatus = sum >= estTotal - ESTIMATE_INVOICED_EPSILON ? 'invoiced' : 'partially_invoiced';
  } else {
    nextStatus = 'invoiced';
  }

  if (est.status === nextStatus) return;
  const { error: upErr } = await updateEstimate(estimateId, { status: nextStatus }, tenantId);
  if (upErr) {
    console.warn('[syncEstimateStatusFromLinkedInvoices] update estimate', upErr);
  }
}

/**
 * Allocate the LOWEST FREE 1-based payment slot for an estimate (partial billing).
 *
 * Reads the full occupied set (every non-voided, non-deleted linked invoice)
 * and returns the smallest slot in 1..MAX_ESTIMATE_PAYMENT_INVOICES that is
 * NOT occupied. `sequence: null` means "all slots full" — that is a normal
 * result, NOT an error (the caller turns it into the user-facing cap message).
 *
 * This is lowest-free, not MAX+1, because a void FREES the slot it held —
 * whichever slot that was. The old MAX+1 form only "rewound" when the voided
 * row happened to hold the HIGHEST slot: with slot 1 voided and slot 2 alive
 * it asked for slot 3, blew the cap of 2, and permanently locked the user out
 * of re-billing (founder hit this in production after voiding the first of two
 * paid payments).
 *
 * Slot REUSE is legal at the DB level: the partial unique index from migration
 * 20260508130000 on (tenant_id, source_estimate_id, estimate_invoice_sequence)
 * excludes voided rows, so a freed slot is genuinely re-insertable — this
 * allocator just has to ask for it.
 *
 * Contract: async (estimateId, tenantId) => { sequence: number|null, error: Error|null }
 */
export const getNextEstimateInvoiceSequence = async (estimateId, tenantId) => {
  if (!tenantId || !estimateId) return { sequence: 1, error: new Error('tenantId and estimateId are required') };
  // No .order()/.limit() — we need the FULL occupied set to find a gap, not the max.
  const { data, error } = await supabase
    .from('invoices')
    .select('estimate_invoice_sequence')
    .eq('tenant_id', tenantId)
    .eq('source_estimate_id', estimateId)
    .neq('status', 'voided')
    .is('deleted_at', null);
  if (error) return { sequence: 1, error };

  const occupied = new Set(
    (data || [])
      .map((row) => row?.estimate_invoice_sequence)
      .filter((seq) => typeof seq === 'number' && Number.isFinite(seq)),
  );
  for (let slot = 1; slot <= MAX_ESTIMATE_PAYMENT_INVOICES; slot += 1) {
    if (!occupied.has(slot)) return { sequence: slot, error: null };
  }
  return { sequence: null, error: null };
};

/**
 * ALL non-voided, non-deleted invoices linked to an estimate, across EVERY year.
 *
 * Deliberately year-unbounded: the dashboards load estimates for all years but
 * invoices for the CURRENT year only, so any client-side convert guard that
 * computes off the in-memory invoices array silently sees ZERO linked invoices
 * for a prior-year partial — a live double-billing path. Callers that gate a
 * convert/re-bill must use this authoritative fetch, not the dashboard arrays.
 *
 * Selects the credit columns too (account_credit_applied,
 * credit_applied_concession, credit_applied_pre_tax, taxable_base,
 * tax_amount, tax_rate) so every consumer can compute `invoicePreCreditTotal`
 * — the "billed against this estimate" figure must count an invoice's face
 * value, not its post-credit net total.
 *
 * Contract: async (estimateId, tenantId) => { invoices: Array<row>, error: Error|null }
 */
export const fetchLinkedInvoicesForEstimate = async (estimateId, tenantId) => {
  if (!tenantId || !estimateId) {
    return { invoices: [], error: new Error('tenantId and estimateId are required') };
  }
  const { data, error } = await supabase
    .from('invoices')
    .select(
      'id, total, status, estimate_invoice_sequence, invoice_number, created_at, due_date, sent_at, ' +
      'account_credit_applied, credit_applied_concession, credit_applied_pre_tax, taxable_base, tax_amount, tax_rate',
    )
    .eq('tenant_id', tenantId)
    .eq('source_estimate_id', estimateId)
    .neq('status', 'voided')
    .is('deleted_at', null);
  if (error) return { invoices: [], error };
  return { invoices: data || [], error: null };
};
